import { useQuery } from '@apollo/client/react';
import { Box, Center, Separator, Spinner } from '@chakra-ui/react';
import { FundingLandingDiagnostic, type LandingDiagnosticHandle } from '@src/components/sco/Funding/FundingLandingDiagnostic';
import { GenericContentBlockConfig, GenericContentBlocks } from '@src/components/sco/GenericLP/GenericContentBlock';
import GenericFAQ from '@src/components/sco/GenericLP/GenericFAQ';
import GenericHero from '@src/components/sco/GenericLP/GenericHero';
import GenericSection from '@src/components/sco/GenericLP/GenericSection';
import GenericSocialProof, { GenericSocialProofItem } from '@src/components/sco/GenericLP/GenericSocialProof';
import GenericStats, { GenericStatItem } from '@src/components/sco/GenericLP/GenericStats';
import { useActiveContentContext } from '@src/contexts/ActiveContentContext';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { useUniversalEffect } from '@src/hooks/useUniversalEffect.tsx';
import { TrackEvent } from '@src/lib/TrackEvent.tsx';
import { gql } from '@src/lib/gql';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { LuChartLine, LuRocket, LuUsers } from 'react-icons/lu';
import InvestorProfileCardAnim from '../../Animations/InvestorMatch/InvestorProfileCardAnim';
import SearchInvestorsAnim from '../../Animations/InvestorMatch/SearchInvestorsAnim';
import VideoWalkthroughCard from '../../InvestorMatching/InvestorMatchingLanding/VideoWalkthroughCard';
import YouTubeVideoCarousel from '../../InvestorMatching/InvestorMatchingLanding/YouTubeVideoCarousel';
const variantKey = 'funding';
const SECTION_BG = 'colorPalette.bg';
const DEMO_VIDEO = 'https://s3.amazonaws.com/app-assets.startups.com/static/videos/ed-trimmed-2.mp4';

// ─── Page content (edit here) ─────────────────────────────────────────────────

const stats: GenericStatItem[] = [{
  icon: LuRocket,
  text: 'Over 1 Million Startups'
}, {
  icon: LuUsers,
  text: '$20B Raised by our Startups'
}, {
  icon: LuChartLine,
  text: '$1B+ Capital Commitments'
}, {
  icon: LuRocket,
  text: '15 IPOs'
}, {
  icon: LuUsers,
  text: '15+ Years of Service'
}];
const buildMatchingBlocks = (openDiagnostic: () => void): GenericContentBlockConfig[] => [{
  heading: 'Stop guessing! We find the best investors for you.',
  paragraph: "Tell us a bit about your startup and we'll do all the heavy lifting to find the most qualified investors for your deal. No more clunky database searches or sketchy investor lists.",
  primaryCta: {
    label: 'Get My Investor Matches',
    onClick: openDiagnostic
  },
  visual: <SearchInvestorsAnim />
}, {
  heading: 'Verified Investors. Ranked with Confidence.',
  paragraph: 'We show you exactly which investors are the best fit for your deal, and which are the most likely to respond when you reach out. We hand-verify each investor to be sure every opportunity is current.',
  primaryCta: {
    label: 'Find Verified Investors',
    onClick: openDiagnostic
  },
  visual: <InvestorProfileCardAnim />
}];
const matchingSocialProof: GenericSocialProofItem[] = [{
  productName: 'Fundable',
  companyName: 'Owlet',
  eyebrow: 'TOTAL RAISED',
  value: '$178,000,000',
  colorPalette: 'blue'
}, {
  productName: 'Launchrock',
  companyName: 'Arcimoto',
  eyebrow: 'TOTAL RAISED',
  value: '$45,000,000',
  colorPalette: 'green'
}, {
  productName: 'Launchrock',
  companyName: 'Airbnb',
  eyebrow: 'TOTAL RAISED',
  value: '$100 Million+',
  colorPalette: 'red'
}];
const buildPitchBlocks = (openDiagnostic: () => void): GenericContentBlockConfig[] => [{
  heading: 'Customized Investor Pitch Emails',
  paragraph: "Not sure what to write in your emails? Don't worry about it, we do. We'll use your existing assets (pitch deck, notes) to write customized emails based on exactly what our target investor needs to see. You'll be able to provide live-updating feedback and modify messages as needed.",
  primaryCta: {
    label: 'Generate My Email Pitch',
    onClick: openDiagnostic
  },
  visual: <VideoWalkthroughCard url={'/assets/videos/investor-match/pitch-builder-demo-1080p.mp4'} />
}, {
  heading: 'Comprehensive Pitch Deck Help',
  paragraph: "Is your Pitch Deck ready for prime time? We can walk you through, slide-by-slide, exactly what you need in your pitch deck and even review it for you before it goes out. You'll have full access to live help at every step.",
  primaryCta: {
    label: 'Get Started',
    onClick: openDiagnostic
  },
  secondaryCta: {
    label: 'Browse Resources',
    href: '/courses'
  },
  visual: <YouTubeVideoCarousel />
}];
const buildOutreachBlocks = (openDiagnostic: () => void): GenericContentBlockConfig[] => [{
  heading: 'Automatic Email Outreach',
  paragraph: "Sending individual, customized emails to every investor is a giant pain in the ass! So we'll just do that for you. Connect your existing email service (Gmail, Outlook, and SMTP/IMAP) and we'll send all of your outreach directly from you so investors can reply directly.",
  primaryCta: {
    label: 'Start Sending Pitches',
    onClick: openDiagnostic
  },
  visual: <VideoWalkthroughCard url={'/assets/videos/investor-match/send-messages.mp4'} />
}, {
  heading: 'Track Every Investor Email',
  paragraph: "We'll provide a report of which emails were sent, who opened them, and who still needs to be followed up with so you can be completely on top of your fundraising campaign.",
  primaryCta: {
    label: 'Start Sending Pitches',
    onClick: openDiagnostic
  },
  visual: <VideoWalkthroughCard url={'/assets/videos/investor-match/activity-replies.mp4'} />
}];
const faqItems = [{
  question: 'How do you actually match me with investors?',
  answer: 'We look at your stage, industry, traction, and raise size, then rank a verified list of the investors most likely to fund a deal like yours.'
}, {
  question: 'Will investors contact me?',
  answer: 'You reach out to them. We put the right deck in front of the right investors, with a message tuned to what they want to see.'
}, {
  question: 'Do I need a pitch deck to get started?',
  answer: 'It helps, and our AI can use it to auto-match and draft outreach. If it isn’t ready, we’ll help you build and review it first.'
}, {
  question: 'How does the automated outreach work?',
  answer: 'Connect Gmail, Outlook, or SMTP/IMAP and we send personalized emails directly from you, so investors reply straight back to you.'
}, {
  question: 'Why would I want your system to email for me?',
  answer: 'Sending individual, customized emails to every investor is a giant time sink. We handle the volume while keeping each one personal.'
}];

// ─── Page ─────────────────────────────────────────────────────────────────────

export const FundingLandingSection = ({
  skipRedirect = false
}: {
  skipRedirect?: boolean;
}) => {
  const router = useRouter();
  const activeContent = useActiveContentContext();
  const {
    currentUser,
    currentUserLoading
  } = useCurrentUser();
  const isRegistered = !!currentUser?.isRegistered;
  const diagnosticRef = useRef<LandingDiagnosticHandle>(null);
  const openDiagnostic = useCallback(() => {
    diagnosticRef.current?.open();
  }, []);
  const matchingBlocks = useMemo(() => buildMatchingBlocks(openDiagnostic), [openDiagnostic]);
  const pitchBlocks = useMemo(() => buildPitchBlocks(openDiagnostic), [openDiagnostic]);
  const outreachBlocks = useMemo(() => buildOutreachBlocks(openDiagnostic), [openDiagnostic]);

  // Funnel resume links include ?signupRequestId= — stay on the landing diagnostic.
  const signupRequestIdParam = router.query.signupRequestId;
  const hasSignupRequestId = Array.isArray(signupRequestIdParam) ? !!signupRequestIdParam[0] : typeof signupRequestIdParam === 'string' ? signupRequestIdParam.length > 0 :
  // asPath is available before isReady; avoids redirecting away while the query hydrates.
  /[?&]signupRequestId=/.test(router.asPath.split('#')[0] || '');
  const suppressRedirect = skipRedirect || hasSignupRequestId;

  // Read-only lookup — do not use useActiveInvestorSearch here; that hook writes
  // ?investorSearchId= into the URL, which would carry onto /investors/matching/[id].
  const searchesQuery = useQuery(gql`
      query FundingLandingRedirectSearches($searchingUserId: String, $excludeEmptyResults: Boolean) {
        scoInvestorSearches(searchingUserId: $searchingUserId, excludeEmptyResults: $excludeEmptyResults) {
          id
          items {
            id
            investorSearchId
          }
        }
      }
    `, {
    skip: suppressRedirect || !isRegistered || !currentUser?.userId,
    fetchPolicy: 'network-only',
    ssr: false,
    variables: {
      excludeEmptyResults: true,
      searchingUserId: currentUser?.userId
    }
  });
  const investorSearchId = searchesQuery.data?.scoInvestorSearches?.items?.find(search => !!search?.investorSearchId)?.investorSearchId;
  const waitingForAuth = currentUserLoading && !currentUser;
  const waitingForSearches = !suppressRedirect && isRegistered && (searchesQuery.loading || !searchesQuery.data);
  // Registered users with an existing fundraise skip the landing into matching —
  // unless resume via signupRequestId or /funding/landing (skipRedirect).
  const shouldRedirect = !suppressRedirect && isRegistered && !waitingForSearches && !!investorSearchId;
  useEffect(() => {
    if (!shouldRedirect || !investorSearchId) {
      return;
    }
    void router.replace(`/investors/matching/${investorSearchId}`);
  }, [shouldRedirect, investorSearchId, router]);
  useUniversalEffect(() => {
    activeContent.setSiteSection('funding.landing');
    activeContent.setItem({
      __typename: 'LandingPage',
      title: 'Raise Capital 10x Faster',
      description: 'Automate your fundraise with AI investor matching and outreach. Match with verified investors, perfect your pitch, and track every email in one place.'
    });
    return () => {
      activeContent.setItem(null);
    };
  }, []);
  if (waitingForAuth || waitingForSearches || shouldRedirect) {
    return <Center flex={1} minHeight={'40vh'} width={'full'}>
        <Spinner colorPalette={'orange'} size={'lg'} />
      </Center>;
  }
  return <Box color={'fg'} width={'full'}>
      <TrackEvent action={'lpViewed'} onlyOnScreen={true} traits={{
      variantKey
    }} />

      <GenericHero gradientText={'10x Faster'} heading={'Raise Capital 10x Faster'} subheading={'Automate your fundraise with investor matching and outreach.'}>
        <FundingLandingDiagnostic ref={diagnosticRef} />
      </GenericHero>

      <GenericStats background={'colorPalette.container/50'} heading={'Key Stats'} stats={stats} />

      <GenericSection background={SECTION_BG} heading={'Match with Ideal Investors'} subheading={"Upload your pitch deck and we'll curate a targeted list of the most relevant investors for your deal."}>
        <GenericContentBlocks blocks={matchingBlocks} />
        <Separator marginY={4} />
        <GenericSocialProof background={SECTION_BG} containerProps={{
        padding: 0
      }} items={matchingSocialProof} />
        {/* <GenericVideo src={DEMO_VIDEO} /> */}
      </GenericSection>

      <GenericSection background={SECTION_BG} heading={'Prepare Your Ideal Pitch'} subheading={'Our Pitch Builder generates individualized emails to send to your matched investors.'}>
        <GenericContentBlocks blocks={pitchBlocks} />
        <Separator marginY={4} />
      </GenericSection>

      <GenericSocialProof background={SECTION_BG} containerProps={{
      padding: 0
    }} items={matchingSocialProof} />

      <GenericSection background={SECTION_BG} heading={'Automated Investor Outreach'} subheading={'Connect your email account and fast-track sending investor pitches with full tracking.'}>
        <GenericContentBlocks blocks={outreachBlocks} />
      </GenericSection>

      <GenericFAQ heading={'Frequent Questions'} items={faqItems} subheading={"Want to know more? Here's some common questions & answers."} />
    </Box>;
};